import React, { useCallback, useEffect, useState, useRef } from "react";
import Cropper from 'react-easy-crop';
import "./cropper.css";
import { getCroppedImg } from "./CroppedImage";
import Button from "../Buttons/Buttons";

const ImageCropper = ({ closeModal, selectedProfileFile, setSelectedProfileFile }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Load the image as a base64 data URL to avoid blob URL lifecycle issues
  const [imageSrc, setImageSrc] = useState(null);
  const readerRef = useRef(null);

  useEffect(() => {
    if (!selectedProfileFile) {
      setImageSrc(null);
      return;
    }
    const reader = new FileReader();
    readerRef.current = reader;
    reader.onload = () => setImageSrc(reader.result);
    reader.onerror = () => setImageSrc(null);
    reader.readAsDataURL(selectedProfileFile);

    return () => {
      // Abort reading if the file changes/unmounts
      if (readerRef.current && readerRef.current.readyState === 1) {
        try { readerRef.current.abort(); } catch (_) {}
      }
      readerRef.current = null;
    };
  }, [selectedProfileFile]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = useCallback(async () => {
    setLoading(true);
    try {
      if (!imageSrc || !croppedAreaPixels) return;
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, 0.7, 500, 500);
      setSelectedProfileFile(croppedImage);
      closeModal();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  }, [croppedAreaPixels, imageSrc, setSelectedProfileFile, closeModal]);

  return (
    <div className="fixed z-50 inset-0 flex justify-center items-center bg-black bg-opacity-50 p-4">
      <div className="crop-dialog relative w-full max-w-[600px] bg-white rounded-lg shadow-lg">
        <div className="crop-container-main flex flex-col">
          <div className="crop-container">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 4}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            )}
          </div>
          <div className="flex mt-4 justify-center items-center">
            <p className="me-2 sm:me-4 font-semibold font-poppins text-sm sm:text-base">Zoom</p>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e) => setZoom(Number(e.target.value))}
              className="zoom-range w-[180px] sm:w-[300px]"
            />
          </div>
        </div>
        <div className="mx-3 sm:mx-[20px] flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-4 pb-3 sm:pb-0">
          <button
            className={`text-white bg-blue-500 w-full sm:w-[200px] hover:shadow-lg text-sm px-4 py-3 my-1 sm:my-2 rounded-lg ${loading || !imageSrc || !croppedAreaPixels ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600 hover:shadow-md"}`}
            onClick={handleCrop}
            disabled={loading || !imageSrc || !croppedAreaPixels}
          >
            {loading ? "Processing..." : "Crop Image"}
          </button>
          <button
            className="text-white bg-blue-500 w-full sm:w-[200px] hover:shadow-lg text-sm px-4 py-3 my-1 sm:my-2 rounded-lg hover:bg-blue-600"
            onClick={() => {
              setSelectedProfileFile(null);
              closeModal()
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
